import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Filter, 
  Search, 
  Clock, 
  FileText, 
  Users, 
  Star,
  Play,
  Eye,
  Calendar,
  Target,
  TrendingUp,
  BookOpen,
  Plus,
  Edit3,
  Check,
  X
} from 'lucide-react';
import { useTest } from '../../contexts/TestContext';
import { useExam } from '../../contexts/ExamContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getMockTests } from '../../utils/firebaseData';
import { getUserTestResults, saveUserTargetScore, getUserTargetScore } from '../../firebase/collections';
import { formatTimestamp } from '../../utils/timestampUtils';

const MockTests = () => {
  const { startTest } = useTest();
  const { selectedExams, activeExam } = useExam();
  const { currentUser } = useRealtime();
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedExamFilter, setSelectedExamFilter] = useState('all');
  
  // Stats state
  const [testResults, setTestResults] = useState([]);
  const [targetScore, setTargetScore] = useState(95);
  const [isEditingTarget, setIsEditingTarget] = useState(false);
  const [tempTargetScore, setTempTargetScore] = useState(95);
  const [savingTarget, setSavingTarget] = useState(false);
  
  const navigate = useNavigate();

  // Load mock tests based on selected exams
  useEffect(() => {
    const loadTests = async () => {
      setLoading(true);
      try {
        let allTests = [];
        
        // Always try to load tests from selected exams first
        if (selectedExams.length > 0) {
          // Combine tests from all selected exams
          for (const exam of selectedExams) {
            const examTests = await getMockTests(exam.id);
            allTests = [...allTests, ...examTests];
          }
        }
        
        // If no tests found from selected exams, show CAT tests as default
        if (allTests.length === 0) {
          allTests = await getMockTests('cat');
        }
        
        setTests(allTests);
        setFilteredTests(allTests);
      } catch (error) {
        setTests([]);
        setFilteredTests([]);
      } finally {
        setLoading(false);
      }
    };

    loadTests();
  }, [selectedExams]);

  // Load test results and target score
  useEffect(() => {
    const loadStats = async () => {
      if (currentUser) {
        try {
          // Load test results
          const resultsResponse = await getUserTestResults(currentUser.uid, 100);
          if (resultsResponse.success && resultsResponse.data) {
            setTestResults(resultsResponse.data);
          }
          
          // Load target score
          const targetResponse = await getUserTargetScore(currentUser.uid);
          if (targetResponse.success && targetResponse.data) {
            setTargetScore(targetResponse.data.targetScore);
            setTempTargetScore(targetResponse.data.targetScore);
          }
        } catch (error) {
          // Error loading stats
        }
      }
    };

    loadStats();
  }, [currentUser]);

  // Filter tests based on search and filters
  useEffect(() => {
    let filtered = tests;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(test =>
        test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.subjects.some(subject => subject.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(test => test.type === selectedType);
    }

    // Difficulty filter
    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(test => test.difficulty === selectedDifficulty);
    }

    // Exam filter
    if (selectedExamFilter !== 'all') {
      filtered = filtered.filter(test => {
        const examIdFromId = (test.id || '').split('-')[0];
        const examIdFromField = test.examId; // present on converted tests
        return examIdFromField === selectedExamFilter || examIdFromId === selectedExamFilter;
      });
    }

    setFilteredTests(filtered);
  }, [tests, searchTerm, selectedType, selectedDifficulty, selectedExamFilter]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'hard': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'full': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400';
      case 'sectional': return 'text-purple-600 bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400';
      case 'live': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400';
      case 'speed': return 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const handleStartTest = (test) => {
    startTest(test);
    // Encode testId to handle spaces and special characters in URLs
    const encodedTestId = encodeURIComponent(test.id);
    navigate(`/dashboard/test/${encodedTestId}`);
  };

  const getExamName = (testId, explicitExamId) => {
    const examId = explicitExamId || (testId || '').split('-')[0];
    const exam = selectedExams.find(e => e.id === examId);
    return exam ? exam.name : examId.toUpperCase();
  };

  // Target score functions
  const handleEditTarget = () => {
    setIsEditingTarget(true);
    setTempTargetScore(targetScore);
  };

  const handleSaveTarget = async () => {
    if (tempTargetScore < 0 || tempTargetScore > 100) {
      return;
    }
    
    setSavingTarget(true);
    try {
      if (currentUser) {
        const result = await saveUserTargetScore(currentUser.uid, tempTargetScore);
        if (result.success) {
          setTargetScore(tempTargetScore);
          setIsEditingTarget(false);
        } else {
        }
      }
    } catch (error) {
      // Error saving target score
    } finally {
      setSavingTarget(false);
    }
  };

  const handleCancelTarget = () => {
    setIsEditingTarget(false);
    setTempTargetScore(targetScore);
  };

  // Calculate real stats - count unique tests (by testId)
  const uniqueTestIds = new Set(
    testResults
      .map(result => result.testId)
      .filter(testId => testId) // Filter out null/undefined
  );
  const testsTaken = uniqueTestIds.size;
  
  // Average score calculated from all attempts (not just unique tests)
  const averageScore = testResults.length > 0 
    ? Math.round(testResults.reduce((acc, result) => acc + (result.percentageScore || result.score || 0), 0) / testResults.length)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white px-1">
            Mock Tests
          </h1>
          <p className="text-xs sm:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1 px-1 leading-relaxed">
            Practice with our comprehensive collection of mock tests
            {selectedExams.length > 0 && (
              <span className="block sm:inline sm:ml-2 text-primary-600 mt-1 sm:mt-0">
                for {selectedExams.map(e => e.name).join(', ')}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <Link to="/dashboard/revision" className="btn-secondary inline-flex items-center justify-center text-sm px-4 py-2.5 min-h-[44px]">
            <BookOpen size={16} className="mr-2 flex-shrink-0" />
            <span>Revision Mode</span>
          </Link>
          <Link to="/dashboard/settings?tab=exams" className="btn-primary inline-flex items-center justify-center text-sm px-4 py-2.5 min-h-[44px]">
            <Plus size={16} className="mr-2 flex-shrink-0" />
            <span>Manage Exams</span>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-3 sm:p-4 lg:p-6"
        >
          <div className="flex items-center">
            <div className="p-2 sm:p-2.5 lg:p-3 bg-primary-100 rounded-lg flex-shrink-0">
              <FileText className="text-primary-600" size={16} />
            </div>
            <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Total Tests</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{tests.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-3 sm:p-4 lg:p-6"
        >
          <div className="flex items-center">
            <div className="p-2 sm:p-2.5 lg:p-3 bg-success-100 rounded-lg flex-shrink-0">
              <Play className="text-success-600" size={16} />
            </div>
            <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Tests Taken</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                {testsTaken}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-3 sm:p-4 lg:p-6"
        >
          <div className="flex items-center">
            <div className="p-2 sm:p-2.5 lg:p-3 bg-warning-100 rounded-lg flex-shrink-0">
              <TrendingUp className="text-warning-600" size={16} />
            </div>
            <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Avg Score</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                {averageScore}%
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-3 sm:p-4 lg:p-6 col-span-2 lg:col-span-1"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0 flex-1">
              <div className="p-2 sm:p-2.5 lg:p-3 bg-purple-100 rounded-lg flex-shrink-0">
                <Target className="text-purple-600" size={16} />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Target Score</p>
                {isEditingTarget ? (
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={tempTargetScore}
                      onChange={(e) => setTempTargetScore(parseInt(e.target.value) || 0)}
                      className="w-12 sm:w-16 px-1 sm:px-2 py-1 text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white bg-transparent border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 text-center"
                    />
                    <span className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">%</span>
                  </div>
                ) : (
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{targetScore}%</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
              {isEditingTarget ? (
                <>
                  <button
                    onClick={handleSaveTarget}
                    disabled={savingTarget}
                    className="p-1.5 sm:p-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
                    title="Save target score"
                  >
                    {savingTarget ? (
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-green-600"></div>
                    ) : (
                      <Check size={14} />
                    )}
                  </button>
                  <button
                    onClick={handleCancelTarget}
                    className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
                    title="Cancel editing"
                  >
                    <X size={14} />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditingTarget(true)}
                  className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
                  title="Edit target score"
                >
                  <Edit3 size={14} />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <div className="card p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col space-y-3 sm:space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search tests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-base sm:text-sm min-h-[44px]"
            />
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {/* Exam Filter */}
            {selectedExams.length > 1 && (
              <select
                value={selectedExamFilter}
                onChange={(e) => setSelectedExamFilter(e.target.value)}
                className="px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-base sm:text-sm min-h-[44px]"
              >
                <option value="all">All Exams</option>
                {selectedExams.map((exam) => (
                  <option key={exam.id} value={exam.id}>
                    {exam.name}
                  </option>
                ))}
              </select>
            )}

            {/* Type Filter */}
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-base sm:text-sm min-h-[44px]"
            >
              <option value="all">All Types</option>
              <option value="full">Full Tests</option>
              <option value="sectional">Sectional Tests</option>
              <option value="live">Live Tests</option>
              <option value="speed">Speed Tests</option>
            </select>

            {/* Difficulty Filter */}
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-base sm:text-sm min-h-[44px]"
            >
              <option value="all">All Difficulties</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 px-1">
            {filteredTests.length} test{filteredTests.length !== 1 ? 's' : ''} found
          </div>
        </div>
      </div>

      {/* Tests Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
        {filteredTests.map((test, index) => (
          <motion.div
            key={test.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card p-4 sm:p-5 lg:p-6 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full"
          >
            {/* Test Header */}
            <div className="flex-shrink-0 mb-3 sm:mb-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start flex-wrap gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base lg:text-lg leading-tight flex-1 min-w-0">
                      {test.name}
                    </h3>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {test.isNew && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full whitespace-nowrap">
                          New
                        </span>
                      )}
                      {test.isLive && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded-full animate-pulse whitespace-nowrap">
                          Live
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                    {test.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Test Details */}
            <div className="flex-shrink-0 space-y-2 mb-3 sm:mb-4">
              <div className="grid grid-cols-3 gap-2 text-xs sm:text-sm">
                <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-gray-600 dark:text-gray-400 mb-1">Duration</div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {Math.floor(test.duration / 3600)}h {Math.floor((test.duration % 3600) / 60)}m
                  </div>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-gray-600 dark:text-gray-400 mb-1">Questions</div>
                  <div className="font-medium text-gray-900 dark:text-white">{test.questions}</div>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-gray-600 dark:text-gray-400 mb-1">Attempts</div>
                  <div className="font-medium text-gray-900 dark:text-white">{test.attempts.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="flex-shrink-0 flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(test.type)} whitespace-nowrap`}>
                {test.type.charAt(0).toUpperCase() + test.type.slice(1)}
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(test.difficulty)} whitespace-nowrap`}>
                {test.difficulty.charAt(0).toUpperCase() + test.difficulty.slice(1)}
              </span>
              {selectedExams.length > 1 && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {getExamName(test.id, test.examId)}
                </span>
              )}
            </div>

            {/* Subjects */}
            <div className="flex-grow mb-3 sm:mb-4">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Subjects:</p>
              <div className="flex flex-wrap gap-1">
                {test.subjects.slice(0, 4).map((subject, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded whitespace-nowrap"
                  >
                    {subject}
                  </span>
                ))}
                {test.subjects.length > 4 && (
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
                    +{test.subjects.length - 4} more
                  </span>
                )}
              </div>
            </div>

            {/* Rating */}
            <div className="flex-shrink-0 flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center space-x-1">
                <Star className="text-yellow-400 fill-current" size={14} />
                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                  {test.rating}
                </span>
              </div>
              {test.lastAttempted && (
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate ml-2">
                  Last: {formatTimestamp(test.lastAttempted, { includeTime: false, short: true })}
                </span>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex-shrink-0 mt-auto">
              <div className="flex flex-col space-y-2">
                {test.isCompleted ? (
                  <Link
                    to={`/dashboard/test-results/${encodeURIComponent(test.id)}`}
                    className="btn-secondary text-center py-2.5 text-sm min-h-[44px] inline-flex items-center justify-center"
                  >
                    <Eye size={16} className="mr-2 flex-shrink-0" />
                    <span>View Result</span>
                  </Link>
                ) : (
                  <button
                    onClick={() => handleStartTest(test)}
                    className="btn-primary py-2.5 text-sm min-h-[44px] inline-flex items-center justify-center"
                  >
                    <Play size={16} className="mr-2 flex-shrink-0" />
                    <span>Start Test</span>
                  </button>
                )}
                
                {test.isLive && test.liveStartTime && (
                  <div className="text-xs text-orange-600 bg-orange-50 dark:bg-orange-900/20 px-2 py-1.5 rounded text-center">
                    Starts {formatTimestamp(test.liveStartTime, { includeTime: true, short: false })}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredTests.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No tests found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Try adjusting your search or filter criteria.
          </p>
          {selectedExams.length === 0 && (
            <div className="mt-4">
              <Link to="/dashboard/settings?tab=exams" className="btn-primary inline-flex items-center">
                <Plus size={16} className="mr-2" />
                Select Exams
              </Link>
            </div>
          )}
          {selectedExams.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                No tests available for your selected exams. Try selecting different exams or check back later.
              </p>
              <Link to="/dashboard/settings?tab=exams" className="btn-secondary inline-flex items-center">
                <Plus size={16} className="mr-2" />
                Change Exam Selection
              </Link>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default MockTests;
